import { useEffect, useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { SnackbarAlert } from 'Components/common/SnackbarAlert';
import { DotsLoader } from 'Components/DotsLoader';
import { Dialog, PageHeader } from 'Components/ui';

import {
  checkDisclaimerInstall,
  createDisclaimerScan,
  createDisclaimerScheduleInstallScan,
  getCookieById,
  getCookieConsentByTenant,
} from 'services/cookies-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';

import { CardIntegrationStatus } from './components/CardIntegrationStatus';
import { CardTotalVisitors } from './components/CardTotalVisitors';
import { CardCookieBannerCode } from './components/CardCookieBannerCode';
import { CardConfigScan } from './components/CardConfigScan';
import { CookiesDetail } from './components/CookiesDetails';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const CARD_SHADOW = '0 1px 3px var(--vo-escuro-8)';

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 30px 0;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 4px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: ${CARD_SHADOW};
  width: fit-content;
`;

const TabBtn = styled.button`
  height: 36px;
  background: ${({ $active }) => $active ? BLUE : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  &:hover:not([data-active="true"]) { background: var(--vo-mist); color: ${INK}; }
`;

const DashGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FieldsCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: ${CARD_SHADOW};
  max-width: 600px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 14px 0;
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
`;

const RowLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .06em;
  align-self: center;
`;

const RowValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ValueText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
`;

const ColorInlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorSwatch = styled.div`
  width: 32px;
  height: 18px;
  background: ${({ $color }) => $color || 'transparent'};
  border-radius: 4px;
  border: 1px solid var(--vo-line);
`;

const InlineEditBtn = styled.button`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  color: ${BLUE};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledThead = styled.thead`
  background: var(--vo-mist);
`;

const ThCell = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
`;

const StyledTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;

const TdCell = styled.td`
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
`;

const IdText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-steel);
`;

const CookieName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
`;

const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
`;

const VersionText = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${BLUE};
`;

const CategoryText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const DateText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${MUTED};
  margin: 0 auto;
  &:hover { background: var(--vo-info-10); color: ${BLUE}; }
`;

const LoadMoreRow = styled.div`
  padding: 12px 20px;
  border-top: 1px solid var(--vo-mist);
  display: flex;
  justify-content: center;
`;

const LoadMoreBtn = styled.button`
  height: 38px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: var(--vo-capri-50); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const getStatusStyle = status => {
  if (status === 'granted') return { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' };
  if (status === 'denied') return { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' };
  return { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' };
};

const getAutoBlockStyle = active => active
  ? { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' }
  : { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };

const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

export const CookiesShow = () => {
  const { t } = useTranslation(['cookies']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const [tabPosition, setTabPosition] = useState(0);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const { isLoading, error, data: cookie } = useQuery('getCookieById', async () => {
    const dataCookie = await getCookieById(id);
    return dataCookie.status === 200 ? dataCookie.data : null;
  });

  const { data: cookieConsent, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['getCookieConsentByTenant'],
    queryFn: async ({ pageParam }) => {
      if (selectedTenant.slug === undefined) return { consents: [], lastId: null };
      const dataCookieConsent = await getCookieConsentByTenant(selectedTenant.slug, pageParam);
      if (dataCookieConsent.status === 200) {
        return { consents: dataCookieConsent.data.consents, lastIds: dataCookieConsent.data.lastIds };
      }
      return { consents: [], lastIds: null };
    },
    getNextPageParam: lastPage => lastPage.lastIds || null,
  });

  const mutationMonthlySchedule = useMutation(
    async disclaimerId => {
      if (disclaimerId == null) throw new Error('Aviso não identificado!');
      return createDisclaimerScan(disclaimerId);
    },
    {
      onError: error => {
        const errorMessage = error.response?.data?.message || error.message;
        Dialog.alert({ title: t('cookies:show.swal.errorTitle'), message: errorMessage, icon: 'error' });
      },
      onSuccess: () => {},
    },
  );

  const mutationScheduleInstallScan = useMutation(
    async disclaimerId => {
      if (disclaimerId == null) throw new Error('Aviso não identificado!');
      return createDisclaimerScheduleInstallScan(disclaimerId);
    },
    { onError: error => { const msg = error.response?.data?.message || error.message; Dialog.alert({ title: t('cookies:show.swal.errorTitle'), message: msg, icon: 'error' }); } },
  );

  const mutationCheckInstall = useMutation(
    disclaimerId => {
      if (disclaimerId == null) throw new Error('Aviso não identificado!');
      return checkDisclaimerInstall(disclaimerId);
    },
    {
      onError: error => {
        const errorMessage = error.response?.data?.message || error.message;
        Dialog.alert({ title: t('cookies:show.swal.errorTitle'), message: errorMessage, icon: 'error' });
      },
      onSuccess: () => {
        Dialog.alert({ title: t('cookies:show.swal.successTitle'), message: t('cookies:show.swal.installVerified'), icon: 'success' });
      },
    },
  );

  useEffect(() => {
    if (mutationMonthlySchedule.isSuccess) {
      Dialog.alert({ title: t('cookies:show.swal.successTitle'), message: t('cookies:show.swal.scanScheduled'), icon: 'success' });
    }
  }, [mutationMonthlySchedule.isSuccess]);

  const handleVerify = async () => {
    await mutationScheduleInstallScan.mutateAsync(id);
    mutationCheckInstall.mutate(id);
  };

  const isVerifying = mutationCheckInstall.isLoading || mutationScheduleInstallScan.isLoading;

  const getPositionBanner = position => {
    if (position === 'left') return t('cookies:show.positions.bottomLeft');
    return t('cookies:show.positions.bottomRight');
  };

  const showDisclaimerDetailModal = item => {
    Dialog.fire({ html: <CookiesDetail name={item.name} cookies={item.cookies} /> });
  };

  const consents = cookieConsent?.pages.flatMap(page => page.consents) ?? [];
  const categories = cookie?.categories || [];

  const tabs = [
    t('cookies:show.tabs.dashboard'),
    t('cookies:show.tabs.positioning'),
    t('cookies:show.tabs.categorization'),
    t('cookies:show.tabs.consent'),
  ];

  return (
    <Container>
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
      {!isLoading && !error && cookie && (
        <PageWrap>
          <PageHeader
            title={cookie.domain ?? ''}
            segments={[
              { label: t('common:breadcrumbs.compliance') },
              { label: t('cookies:page.breadcrumbLabel'), route: '/cookies' },
              { label: cookie.domain ?? '' },
            ]}
          />

          <TabsRow>
            {tabs.map((label, i) => (
              <TabBtn key={i} $active={tabPosition === i} data-active={tabPosition === i} onClick={() => setTabPosition(i)}>
                {label}
              </TabBtn>
            ))}
          </TabsRow>

          {/* Dashboard tab */}
          {tabPosition === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <DashGrid>
                <CardIntegrationStatus
                  disclaimerStatusAtivo={cookie.disclaimerStatusAtivo}
                  domain={cookie.domain}
                  onVerify={handleVerify}
                  isVerifying={isVerifying}
                />
                <CardTotalVisitors
                  totalVisitors={cookie.totalVisitors}
                  optIn={cookie.totalOptIn}
                  optOut={cookie.totalOptOut}
                />
              </DashGrid>
              <CardCookieBannerCode id={id} setShowCopySuccess={setShowCopySuccess} />
              {cookie.disclaimerStatusAtivo && (
                <CardConfigScan
                  id={id}
                  disclaimerMonthlySchedule={cookie.disclaimerMonthlySchedule}
                  mutationMonthlySchedule={mutationMonthlySchedule}
                />
              )}
            </div>
          )}

          {/* Positioning tab */}
          {tabPosition === 1 && (
            <FieldsCard>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.site')}</RowLabel>
                <RowValue>
                  <ValueText>{cookie.domain}</ValueText>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.bgColor')}</RowLabel>
                <RowValue>
                  <ColorInlineRow>
                    <ValueText>{cookie.background_color}</ValueText>
                    <ColorSwatch $color={cookie.background_color} />
                  </ColorInlineRow>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.primaryColor')}</RowLabel>
                <RowValue>
                  <ColorInlineRow>
                    <ValueText>{cookie.primary_color}</ValueText>
                    <ColorSwatch $color={cookie.primary_color} />
                  </ColorInlineRow>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.textColor')}</RowLabel>
                <RowValue>
                  <ColorInlineRow>
                    <ValueText>{cookie.text_color}</ValueText>
                    <ColorSwatch $color={cookie.text_color} />
                  </ColorInlineRow>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.defaultLanguage')}</RowLabel>
                <RowValue>
                  <ValueText>{cookie.language}</ValueText>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
              <FieldRow>
                <RowLabel>{t('cookies:show.fields.bannerPosition')}</RowLabel>
                <RowValue>
                  <ValueText>{getPositionBanner(cookie.position_controller)}</ValueText>
                  <InlineEditBtn onClick={() => navigate('/cookies/edit/' + id)}>
                    <EditIcon />{t('cookies:show.fields.editButton')}
                  </InlineEditBtn>
                </RowValue>
              </FieldRow>
            </FieldsCard>
          )}

          {/* Categorization tab */}
          {tabPosition === 2 && (
            <TableCard>
              <StyledTable>
                <StyledThead>
                  <tr>
                    <ThCell style={{ width: 80 }}>{t('cookies:show.table.category.id')}</ThCell>
                    <ThCell>{t('cookies:show.table.category.cookie')}</ThCell>
                    <ThCell $center>{t('cookies:show.table.category.autoBlock')}</ThCell>
                    <ThCell $center>{t('cookies:show.table.category.details')}</ThCell>
                  </tr>
                </StyledThead>
                <tbody>
                  {categories.map((ck, i) => {
                    const ab = getAutoBlockStyle(ck.auto_block);
                    return (
                      <StyledTr key={ck.id || i}>
                        <TdCell><IdText>{ck.id}</IdText></TdCell>
                        <TdCell><CookieName>{ck.name}</CookieName></TdCell>
                        <TdCell $center>
                          <Badge $color={ab.color} $bg={ab.bg}>
                            {ck.auto_block ? t('cookies:show.table.category.yes') : t('cookies:show.table.category.no')}
                          </Badge>
                        </TdCell>
                        <TdCell $center>
                          <ActionBtn title={t('cookies:show.table.category.details')} onClick={() => showDisclaimerDetailModal(ck)}>
                            <EyeIcon />
                          </ActionBtn>
                        </TdCell>
                      </StyledTr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </TableCard>
          )}

          {/* Consent tab */}
          {tabPosition === 3 && (
            <TableCard>
              <StyledTable>
                <StyledThead>
                  <tr>
                    <ThCell>{t('cookies:show.table.consent.id')}</ThCell>
                    <ThCell>{t('cookies:show.table.consent.status')}</ThCell>
                    <ThCell>{t('cookies:show.table.consent.categories')}</ThCell>
                    <ThCell>{t('cookies:show.table.consent.version')}</ThCell>
                    <ThCell>{t('cookies:show.table.consent.createdAt')}</ThCell>
                  </tr>
                </StyledThead>
                <tbody>
                  {consents.map((c, i) => {
                    const st = getStatusStyle(c.status);
                    const cats = (() => {
                      const raw = c.categories;
                      if (!raw) return '—';
                      if (typeof raw === 'string') return raw;
                      if (Array.isArray(raw)) return raw.join(', ');
                      const active = Object.keys(raw).filter(k => raw[k]);
                      return active.length > 0 ? active.join(', ') : '—';
                    })();
                    return (
                      <StyledTr key={c.id || i}>
                        <TdCell><IdText>{c.id}</IdText></TdCell>
                        <TdCell>
                          <Badge $color={st.color} $bg={st.bg}>{c.status}</Badge>
                        </TdCell>
                        <TdCell><CategoryText>{cats}</CategoryText></TdCell>
                        <TdCell><VersionText>v{c.version || '1'}</VersionText></TdCell>
                        <TdCell><DateText>{formatDateTimeToDateTimeBR(c.createdAt)}</DateText></TdCell>
                      </StyledTr>
                    );
                  })}
                </tbody>
              </StyledTable>
              <LoadMoreRow>
                <LoadMoreBtn onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
                  {isFetchingNextPage ? <DotsLoader /> : t('cookies:show.loadMore')}
                </LoadMoreBtn>
              </LoadMoreRow>
            </TableCard>
          )}
        </PageWrap>
      )}
      <SnackbarAlert
        open={showCopySuccess}
        onClose={() => setShowCopySuccess(false)}
        severity="success"
        message={t('cookies:show.copied')}
        autoHideDuration={3000}
      />
    </Container>
  );
};
